/**
 * Signature normalization — the crux of Epic 11's "reuse, don't regenerate"
 * guarantee (VISION §6). The signature is the hit/miss boundary: too strict and
 * the catalog never hits (everything regenerates, no cost win); too loose and a
 * wrong preset is reused (broken animations). It is deliberately built from the
 * *intent* of an effect, not its target, and NOT its timing magnitude:
 *
 *   action + direction + normalized from/to + easing
 *
 * `elementNumber` is intentionally NOT part of the signature — the same effect on
 * a different element reuses the same preset.
 *
 * **Timing magnitude is NOT in the signature (DBG-019).** `durationMs`/`delayMs`
 * are per-instance PARAMETERS (CSS `animation-duration`/`-delay`), not part of the
 * keyframe shape — so the same fade reused at 2s / 0.8s / 0.3s must collide on ONE
 * preset, with the duration applied at resolve time (`resolvePresetToAnimation`
 * overlays the instruction's timing). Keying on duration made every duration a
 * cache MISS → the coder re-ran at full cost, defeating the reuse guarantee. The
 * signature still keeps semantically-distinct effects apart (slideLeft ≠
 * slideRight, fade-in ≠ fade-out, ease-in-out ≠ linear).
 *
 * Documented buckets:
 * - **from / to numeric state** → each numeric property rounded to
 *   {@link STATE_PRECISION} (2) decimal places; `undefined` properties dropped;
 *   keys sorted for a stable serialization. Absorbs float precision noise without
 *   collapsing meaningfully different magnitudes.
 * - **easing** → lowercased and whitespace-collapsed (so `"ease-in-out"` and
 *   `"Ease-In-Out"` / `"cubic-bezier(0.4, 0, 0.2, 1)"` with stray spaces canonicalize).
 *
 * Pure and deterministic: same intent in → same signature out, this run or next.
 */
/**
 * Timing bucket size, kept as a public constant for callers that bucket timing
 * for display/telemetry. As of DBG-019 it is NO LONGER used by the signature —
 * timing magnitude is not part of the catalog key.
 */
export const TIMING_BUCKET_MS = 100;
/** Numeric `from`/`to` properties round to this many decimal places. */
export const STATE_PRECISION = 2;
function canonicalizeEasing(easing) {
    if (!easing)
        return '';
    return easing.trim().toLowerCase().replace(/\s+/g, '');
}
/** Round to STATE_PRECISION and strip `-0` so `0` and `-0` serialize identically. */
function roundState(value) {
    const factor = 10 ** STATE_PRECISION;
    const rounded = Math.round(value * factor) / factor;
    return rounded === 0 ? 0 : rounded;
}
/**
 * Normalize a property state into a sorted, rounded plain object so two
 * structurally-equal states serialize byte-identically regardless of key order
 * or float noise. `undefined` properties are dropped.
 */
function normalizeState(state) {
    if (!state)
        return {};
    const out = {};
    for (const key of Object.keys(state).sort()) {
        const value = state[key];
        if (value !== undefined && Number.isFinite(value)) {
            out[key] = roundState(value);
        }
    }
    return out;
}
function isPreset(input) {
    return 'type' in input && 'timing' in input;
}
function toIntent(input) {
    if (isPreset(input)) {
        const directionParam = input.params?.direction;
        return {
            action: input.type,
            direction: typeof directionParam === 'string' ? directionParam : '',
            from: normalizeState(input.from),
            to: normalizeState(input.to),
            easing: canonicalizeEasing(input.timing.easing),
        };
    }
    const { action, params } = input;
    return {
        action,
        direction: params.direction ?? '',
        from: normalizeState(params.from),
        to: normalizeState(params.to),
        easing: canonicalizeEasing(params.easing),
    };
}
/**
 * Compute the normalized signature for a structured instruction or a preset.
 * An instruction and the preset that satisfies it produce the SAME signature
 * (state rounded, timing excluded), which is what makes catalog lookup work and
 * lets one preset serve any duration/delay (DBG-019).
 */
export function signatureOf(input) {
    const intent = toIntent(input);
    // JSON.stringify over a field-ordered object is a stable serialization: the
    // top-level keys are written in source order, and nested state objects were
    // pre-sorted by `normalizeState`.
    return JSON.stringify({
        action: intent.action,
        direction: intent.direction,
        from: intent.from,
        to: intent.to,
        easing: intent.easing,
    });
}
